import axios from 'axios';
import { Course, TeamMember, StudentTestimonial, Resource } from '../types';

// const API_URL = 'http://68.183.84.248/api';
const API_URL = 'https://onlinestudyroom.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Create a separate instance for public endpoints
const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Course API calls
export const courseApi = {
  getAllCourses: () => api.get('/courses'),
  getCourseById: (id: string) => api.get(`/courses/${id}`),
  getSubscribedCourses: () => api.get('/courses/subscribed'),
  createCourse: (courseData: Partial<Course>) => api.post('/courses', courseData),
  updateCourse: (id: string, courseData: Partial<Course>) => api.put(`/courses/${id}`, courseData),
  deleteCourse: (id: string) => api.delete(`/courses/${id}`),
  // New endpoints for course details
  getCourseDetail: (courseId: string) => api.get(`/courses/${courseId}/details`),
  createCourseDetail: (courseId: string, data: any) => api.post(`/courses/${courseId}/details`, data),
  updateCourseDetail: (courseId: string, data: any) => api.put(`/courses/${courseId}/details`, data),
  deleteCourseDetail: async (courseId: string) => {
    return await api.delete(`/api/courses/${courseId}/details`);
  },
};

// CourseV2 API calls
export const courseV2Api = {
  getAll: () => api.get('/coursesv2'),
  getById: (id: string) => api.get(`/coursesv2/${id}`),
  getByExamType: (examType: string) => api.get(`/coursesv2/by-exam/${examType}`),
  create: (data: any) => api.post('/coursesv2', data),
  update: (id: string, data: any) => api.put(`/coursesv2/${id}`, data),
  delete: (id: string) => api.delete(`/coursesv2/${id}`),
};

// Resource API calls
export const resourceApi = {
  // Public endpoints
  getAllResources: () => publicApi.get<{ status: string; results: number; data: { resources: Resource[] } }>('/resources'),
  getResourceById: (id: string) => publicApi.get<{ status: string; data: { resource: Resource } }>(`/resources/${id}`),
  
  // Protected endpoints (require authentication)
  createResource: (resourceData: Partial<Resource>) => api.post<{ status: string; data: { resource: Resource } }>('/resources', resourceData),
  updateResource: (id: string, resourceData: Partial<Resource>) => api.put<{ status: string; data: { resource: Resource } }>(`/resources/${id}`, resourceData),
  deleteResource: (id: string) => api.delete(`/resources/${id}`),
};

// Auth API calls
export const authApi = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (name: string, email: string, password: string) => api.post('/auth/register', { name, email, password }),
  logout: () => {
    localStorage.removeItem('token');
  },
};

export const teamApi = {
  getAll: () => publicApi.get<{ status: string; results: number; data: { members: TeamMember[] } }>('/team'),
  create: (member: Partial<TeamMember>) => api.post('/team', member),
  update: (id: string, member: Partial<TeamMember>) => api.put(`/team/${id}`, member),
  delete: (id: string) => api.delete(`/team/${id}`),
};

export const aboutUsApi = {
  getAboutUs: () => publicApi.get('/content/about-us'),
  updateAboutUs: (data: { history: string; values: { title: string; description: string }[] }) => api.put('/content/about-us', data),
};

export const studentTestimonialApi = {
  getAll: () => publicApi.get<{ status: string; results: number; data: { testimonies: StudentTestimonial[] } }>('/testimonies'),
  create: (data: Partial<StudentTestimonial>) => api.post('/testimonies', data),
  update: (id: string, data: Partial<StudentTestimonial>) => api.put(`/testimonies/${id}`, data),
  delete: (id: string) => api.delete(`/testimonies/${id}`),
};

export const contactApi = {
  sendContact: (data: { name: string; email?: string; mobile?: string; course: string; message: string }) => publicApi.post('/contact', data),
  getAll: () => api.get('/contact'), // admin only
  exportToExcel: () => api.get('/contact/export', { 
    responseType: 'blob',
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  }), // admin only - downloads XLSX file
};

export default api; 